import { useCallback } from "react";
import { ref, uploadString, getDownloadURL } from "firebase/storage";
import { collection, addDoc } from "firebase/firestore";
import { storage, firestore } from "../firebase";
import { useData } from "./useData";

export const useFirebase = () => {
  const { canvas, user, setSnackbar, setLoading, setError } = useData();

  const saveDesign = useCallback(async () => {
    const effectiveUserId = user?.uid || "guest";
    setLoading(true);
    try {
      const timestamp = Date.now();
      // Save JSON
      const jsonRef = ref(
        storage,
        `designs/${effectiveUserId}/${timestamp}.json`
      );
      const json = JSON.stringify(canvas.toJSON(["id", "name"]));
      await uploadString(jsonRef, json);

      // Save PNG
      const pngRef = ref(
        storage,
        `designs/${effectiveUserId}/${timestamp}.png`
      );
      const pngData = canvas.toDataURL({ format: "png", quality: 1 });
      await uploadString(pngRef, pngData, "data_url");
      const pngUrl = await getDownloadURL(pngRef);

      setSnackbar({
        open: true,
        message: "Design saved successfully!",
        severity: "success",
      });
      return { jsonPath: jsonRef.fullPath, pngUrl };
    } catch (error) {
      setError("Failed to save design: " + error.message);
      setSnackbar({
        open: true,
        message: "Failed to save design.",
        severity: "error",
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [canvas, user, setSnackbar, setLoading, setError]);

  const loadDesign = useCallback(
    async (jsonPath) => {
      const effectiveUserId = user?.uid || "guest";
      if (!effectiveUserId || !canvas) {
        setSnackbar({
          open: true,
          message: "Please initialize canvas.",
          severity: "error",
        });
        return;
      }

      setLoading(true);
      try {
        const jsonUrl = await getDownloadURL(ref(storage, jsonPath));
        const response = await fetch(jsonUrl);
        const json = await response.json();
        canvas.loadFromJSON(json, () => {
          canvas.renderAll();
          setSnackbar({
            open: true,
            message: "Design loaded successfully!",
            severity: "success",
          });
        });
      } catch (error) {
        setError("Failed to load design: " + error.message);
        setSnackbar({
          open: true,
          message: "Failed to load design.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [canvas, user, setSnackbar, setLoading, setError]
  );

  const saveProduct = useCallback(
    async (designData, productData) => {
      const effectiveUserId = user?.uid || "guest";
      setLoading(true);
      try {
        await addDoc(collection(firestore, "products"), {
          userId: effectiveUserId,
          designUrl: designData.pngUrl,
          jsonPath: designData.jsonPath,
          productCode: productData.productCode,
          name: productData.name,
          size:
            productData.size ||
            productData.product_attributes?.sizes?.options[0]?.name,
          attributes: productData.product_attributes || {},
          createdAt: new Date(),
        });
        setSnackbar({
          open: true,
          message: "Product saved successfully!",
          severity: "success",
        });
      } catch (error) {
        setError("Failed to save product: " + error.message);
        setSnackbar({
          open: true,
          message: "Failed to save product.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [user, setSnackbar, setLoading, setError]
  );

  return { saveDesign, loadDesign, saveProduct };
};
